import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHeader from '../components/PageHeader';
import { contactInfo, founder } from '../mock';
import { Mail, Phone, MapPin, Clock, ArrowUpRight, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({ title: 'Message sent!', description: 'Our team will reach out within 24 hours.' });
    setForm({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  return (
    <div className="font-body bg-paper text-[#1c1c1c]">
      <Navbar />
      <PageHeader
        eyebrow="Contact Us"
        title={<>Talk to our <em className="italic">counselors</em></>}
        description="Whether you need clarity on the course, eligibility, or placements — our team is ready to help. Let’s get you certified."
        breadcrumb="Contact"
      />

      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
            {/* Left: contact info */}
            <div className="lg:col-span-2 space-y-4">
              <div className="card-soft rounded-2xl p-6">
                <h4 className="font-body font-semibold text-[#143324] mb-1">Reach out to us</h4>
                <p className="text-[13px] text-[#3d3d3d] font-body mb-3">Drop us a message anytime.</p>
                <a href={`mailto:${contactInfo.email}`} className="inline-flex items-center gap-2 text-[#143324] font-body hover:gap-3 transition-all">
                  <Mail size={16} /> {contactInfo.email}
                </a>
              </div>
              <div className="card-soft rounded-2xl p-6">
                <h4 className="font-body font-semibold text-[#143324] mb-1">Call us</h4>
                <p className="text-[13px] text-[#3d3d3d] font-body mb-3">We’re here to help — give us a call.</p>
                <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-2 text-[#143324] font-body hover:gap-3 transition-all">
                  <Phone size={16} /> {contactInfo.phone}
                </a>
              </div>
              <div className="card-soft rounded-2xl p-6">
                <h4 className="font-body font-semibold text-[#143324] mb-3 flex items-center gap-2"><Clock size={16} /> Business Hours</h4>
                <ul className="space-y-1.5 text-[13px] text-[#3d3d3d] font-body">
                  {contactInfo.hours.map((h, i) => (
                    <li key={i} className="flex items-center justify-between">
                      <span>{h.day}</span>
                      <span className="text-[#143324]">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card-soft rounded-2xl overflow-hidden">
                <img src={contactInfo.officeImage} alt="Office" className="w-full h-40 object-cover" />
                <div className="p-5">
                  <h4 className="font-body font-semibold text-[#143324] mb-3 flex items-center gap-2">
                    <MapPin size={15} /> Our Offices
                  </h4>
                  <div className="space-y-3">
                    {contactInfo.offices.map((office) => (
                      <div key={office.city} className="rounded-xl bg-[#f5f1e6] border border-[#143324]/10 p-3">
                        <p className="font-body font-semibold text-[#143324] text-[14px] mb-1">{office.city}</p>
                        <p className="text-[12.5px] leading-relaxed text-[#3d3d3d] font-body">{office.address}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="card-soft rounded-2xl p-6 md:p-8">
                <h3 className="font-serif-display text-[#143324] text-3xl md:text-4xl mb-6">Send us a message</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[12px] text-[#3d3d3d] font-body mb-1.5">Name*</label>
                    <input required name="name" value={form.name} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-[#143324]/15 bg-[#f5f1e6] text-[#1c1c1c] font-body outline-none focus:border-[#143324]/40" />
                  </div>
                  <div>
                    <label className="block text-[12px] text-[#3d3d3d] font-body mb-1.5">Email*</label>
                    <input required type="email" name="email" value={form.email} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-[#143324]/15 bg-[#f5f1e6] text-[#1c1c1c] font-body outline-none focus:border-[#143324]/40" />
                  </div>
                  <div>
                    <label className="block text-[12px] text-[#3d3d3d] font-body mb-1.5">Phone</label>
                    <input name="phone" value={form.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-[#143324]/15 bg-[#f5f1e6] text-[#1c1c1c] font-body outline-none focus:border-[#143324]/40" />
                  </div>
                  <div>
                    <label className="block text-[12px] text-[#3d3d3d] font-body mb-1.5">Subject</label>
                    <select name="subject" value={form.subject} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-[#143324]/15 bg-[#f5f1e6] text-[#1c1c1c] font-body outline-none focus:border-[#143324]/40">
                      <option value="">Choose a topic…</option>
                      <option>EA Course Enquiry</option>
                      <option>US Taxation Training</option>
                      <option>Business Setup</option>
                      <option>Placement / Career</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[12px] text-[#3d3d3d] font-body mb-1.5">Message*</label>
                    <textarea required name="message" value={form.message} onChange={handleChange} rows={5} className="w-full px-4 py-3 rounded-lg border border-[#143324]/15 bg-[#f5f1e6] text-[#1c1c1c] font-body outline-none focus:border-[#143324]/40 resize-y" />
                  </div>
                </div>
                <button type="submit" className="mt-6 inline-flex items-center gap-2 btn-primary px-6 py-3 rounded-lg text-[14px] font-body">
                  Submit <ArrowUpRight size={15} />
                </button>
              </form>

              {/* Reply card */}
              <div className="mt-6 card-soft rounded-2xl p-6 flex items-center gap-4">
                <img src={founder.image} alt="counselor" className="w-14 h-14 rounded-full object-cover" />
                <div className="flex-1">
                  <p className="text-[13px] text-[#3d3d3d] font-body">Replies within 24 hours</p>
                  <p className="font-body font-semibold text-[#143324] flex items-center gap-2">
                    <span className="inline-flex w-2 h-2 rounded-full bg-green-500" /> AVAILABLE NOW
                  </p>
                </div>
                <a href="https://wa.me/917010492161" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 btn-outline px-4 py-2 rounded-full text-[13px] font-body">
                  <MessageCircle size={14} /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
